import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import OptionRow from './OptionRow'
import { pluralize } from '../../../utils/textHelpers'

const TRIGGER_BASE = 'tw:flex tw:items-center tw:justify-between tw:gap-2 tw:w-full '
  + 'tw:px-3 tw:py-2 tw:bg-white tw:border tw:border-solid '
  + 'tw:rounded! tw:cursor-pointer tw:text-sm tw:text-[#333333]'

const TRIGGER_BORDER = 'tw:border-[#cccccc] tw:hover:border-[#b3b3b3]'
const TRIGGER_BORDER_OPEN = 'tw:border-ds-primary'

const TRIGGER_DISABLED = 'tw:opacity-60 tw:cursor-not-allowed'

const PANEL_CLASSES = 'tw:absolute tw:top-full tw:left-0 tw:right-0 tw:mt-1 tw:bg-white '
  + 'tw:rounded-md! tw:shadow-[0_0_7px_0_rgba(0,0,0,0.1)] tw:z-50 '
  + 'tw:max-h-80 tw:overflow-hidden tw:flex tw:flex-col'

const SEARCH_WRAP = 'tw:p-2'

const SEARCH_INPUT = 'tw:w-full tw:px-2 tw:py-1 tw:border tw:border-solid tw:border-gray-300 '
  + 'tw:rounded tw:text-sm tw:focus:outline-none tw:focus:border-ds-primary'

const CHEVRON_PATH = 'M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 '
  + '1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 '
  + '0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s'
  + '-0.436-1.17 0-1.615z'

const CHEVRON_BASE = 'tw:shrink-0 tw:transition-colors'
const CHEVRON_CLOSED = 'tw:text-[#cccccc] tw:hover:text-[#999999]'
const CHEVRON_OPEN = 'tw:text-[#666666] tw:hover:text-[#333333]'

const formatDefaultSelectedLabel = count => pluralize(count, 'sélectionné')

const DESELECT_ALL = 'tw:flex tw:items-center tw:px-3 tw:py-2 tw:w-full tw:text-left '
  + 'tw:bg-transparent tw:border-0 tw:cursor-pointer tw:text-sm tw:font-medium '
  + 'tw:text-ds-primary tw:rounded! tw:hover:bg-gray-100'

const selectedFirst = (options, selectedSet) => {
  const head = options.filter(option => selectedSet.has(option.value))
  const tail = options.filter(option => !selectedSet.has(option.value))
  return [...head, ...tail]
}

const MultiSelectPicker = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  searchPlaceholder,
  noOptionsMessage,
  formatSelectedLabel,
  maxSelection,
  maxReachedMessage,
  deselectAllLabel,
  onDeselectAll,
  disabled
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [orderedOptions, setOrderedOptions] = useState(options)
  const containerRef = useRef(null)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target))
        setIsOpen(false)
    }
    const handleKeyDown = event => {
      if (event.key === 'Escape')
        setIsOpen(false)
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const filteredOptions = useMemo(() => {
    if (!search.trim())
      return orderedOptions
    const needle = search.toLowerCase()
    return orderedOptions.filter(option => option.label.toLowerCase().includes(needle))
  }, [orderedOptions, search])

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])

  useEffect(() => {
    if (!isOpen)
      setOrderedOptions(options)
  }, [isOpen, options])

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (justOpened)
      setOrderedOptions(current => selectedFirst(current, selectedSet))
  }, [isOpen, selectedSet])

  const isAtMax = maxSelection !== null && selectedValues.length >= maxSelection

  const toggleValue = useCallback(value => {
    if (selectedSet.has(value)) {
      onChange(selectedValues.filter(current => current !== value))
      return
    }
    if (isAtMax)
      return
    onChange([...selectedValues, value])
  }, [selectedSet, selectedValues, onChange, isAtMax])

  const handleToggleOpen = useCallback(() => {
    if (disabled)
      return
    setIsOpen(prev => !prev)
  }, [disabled])

  const handleSearchChange = useCallback(event => setSearch(event.target.value), [])

  const triggerLabel = selectedValues.length === 0 ? placeholder : formatSelectedLabel(selectedValues.length)
  const triggerBorder = isOpen ? TRIGGER_BORDER_OPEN : TRIGGER_BORDER

  return (
    <div ref={containerRef} className="tw:relative">
      <button
        className={`${TRIGGER_BASE} ${triggerBorder} ${disabled ? TRIGGER_DISABLED : ''}`}
        disabled={disabled}
        type="button"
        onClick={handleToggleOpen}
      >
        <span>{triggerLabel}</span>
        <svg
          aria-hidden="true"
          className={`${CHEVRON_BASE} ${isOpen ? CHEVRON_OPEN : CHEVRON_CLOSED}`}
          focusable="false"
          height="20"
          viewBox="0 0 20 20"
          width="20"
        >
          <path
            d={CHEVRON_PATH}
            fill="currentColor"
          />
        </svg>
      </button>
      {isOpen ? (
        <div className={PANEL_CLASSES}>
          <div className={SEARCH_WRAP}>
            <input
              className={SEARCH_INPUT}
              placeholder={searchPlaceholder}
              type="text"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          {deselectAllLabel && onDeselectAll && selectedValues.length > 0 ? (
            <button className={DESELECT_ALL} type="button" onClick={onDeselectAll}>
              {deselectAllLabel}
            </button>
          ) : null}
          {isAtMax && maxReachedMessage ? (
            <div className="tw:px-3 tw:py-2 tw:text-xs tw:text-ds-warning tw:bg-ds-warning-lowest">
              {maxReachedMessage}
            </div>
          ) : null}
          <div className="tw:overflow-y-auto tw:flex-1">
            {filteredOptions.length === 0 ? (
              <div className="tw:p-3 tw:text-sm tw:text-ds-neutral-high tw:text-center">
                {noOptionsMessage}
              </div>
            ) : filteredOptions.map(option => {
              const isChecked = selectedSet.has(option.value)
              const isDisabled = !isChecked && isAtMax
              return (
                <OptionRow
                  key={option.value}
                  isChecked={isChecked}
                  isDisabled={isDisabled}
                  option={option}
                  onToggle={toggleValue}
                />
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

MultiSelectPicker.propTypes = {
  deselectAllLabel: PropTypes.string,
  disabled: PropTypes.bool,
  formatSelectedLabel: PropTypes.func,
  maxReachedMessage: PropTypes.string,
  maxSelection: PropTypes.number,
  noOptionsMessage: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onDeselectAll: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  })).isRequired,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  selectedValues: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired
}

MultiSelectPicker.defaultProps = {
  deselectAllLabel: null,
  disabled: false,
  formatSelectedLabel: formatDefaultSelectedLabel,
  maxReachedMessage: null,
  maxSelection: null,
  noOptionsMessage: 'Aucune option',
  onDeselectAll: null,
  placeholder: 'Sélectionner',
  searchPlaceholder: 'Rechercher'
}

export default MultiSelectPicker
